import React, { useState, useEffect, useRef, useCallback } from 'react';
import { alertsAPI } from '../services/api';
import AIAnalysis from '../components/AIAnalysis';
import Troubleshoot from '../components/Troubleshoot';
import ResolutionPlan from '../components/ResolutionPlan';
import PlaybookFormModal from '../components/PlaybookFormModal';
import './AlertsPage.css';

const VERDICT_OPTIONS = [
  { value: 'correct',   label: 'AI was correct' },
  { value: 'partial',   label: 'Partially correct' },
  { value: 'incorrect', label: 'AI was wrong' },
];

// One AI-diagnosed field (root_cause.summary or .details) with an Accept/Edit toggle.
// Accept = the AI's original text is used unedited (correction sent as null so the
// backend knows nothing was overridden); Edit reveals a textarea pre-filled with the
// AI's text for a full rewrite.
const CorrectableField = ({ label, aiValue, editing, setEditing, text, setText }) => {
  if (!aiValue) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>{label}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className={`btn btn-sm ${!editing ? 'btn-tinted-success' : 'btn-secondary'}`}
            style={{ height: 22, padding: '0 8px', fontSize: 11 }}
          >
            Accept
          </button>
          <button
            type="button"
            onClick={() => { setEditing(true); if (!text) setText(aiValue); }}
            className={`btn btn-sm ${editing ? 'btn-accent' : 'btn-secondary'}`}
            style={{ height: 22, padding: '0 8px', fontSize: 11 }}
          >
            Edit
          </button>
        </div>
      </div>
      {editing ? (
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
          className="textarea"
          style={{ resize: 'vertical', width: '100%' }}
        />
      ) : (
        <p style={{ fontSize: 13, color: 'var(--ink-1)', margin: 0, padding: '4px 0' }}>{aiValue}</p>
      )}
    </div>
  );
};

const MarkResolved = ({ alert, onResolved }) => {
  const [open, setOpen]         = useState(false);
  const [notes, setNotes]       = useState('');
  const [verdict, setVerdict]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(alert.resolved_confirmed || false);

  const rootCause   = alert.ai_analysis?.root_cause || {};
  const hasRootCause = !!(rootCause.summary || rootCause.details);

  const [summaryEditing, setSummaryEditing] = useState(false);
  const [summaryText, setSummaryText]       = useState('');
  const [detailsEditing, setDetailsEditing] = useState(false);
  const [detailsText, setDetailsText]       = useState('');

  const isAutoResolved = alert.auto_resolved && !alert.resolved_confirmed;

  // Required: can't confirm without picking a verdict on the AI's diagnosis.
  const canConfirm = !!verdict;

  const handleResolve = async () => {
    if (!canConfirm) return;
    setSaving(true);
    try {
      const correction = hasRootCause ? {
        summary: summaryEditing ? summaryText : null,
        details: detailsEditing ? detailsText : null,
      } : null;
      await alertsAPI.resolve(alert.alert_id, notes, verdict, correction);
      setDone(true);
      setOpen(false);
      if (onResolved) onResolved();
    } catch (e) {
      window.alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  // Already human-confirmed
  if (done) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', color: 'var(--color-success-500)', fontWeight: 600, fontSize: 13 }}>
        <span>✓</span> Verified & saved to knowledge base
      </div>
    );
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
      {/* Yellow banner for auto-resolved alerts pending human verification */}
      {isAutoResolved && !open && (
        <div className="banner warning" style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 16, marginTop: 1 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-warning-700)' }}>
              Auto-resolved by Prometheus — human verification required
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-warning-700)', marginTop: 3 }}>
              This alert resolved on its own. Review the AI analysis below and confirm whether
              its diagnosis was accurate. Unverified resolutions are never stored.
            </div>
          </div>
        </div>
      )}

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className={`btn btn-sm ${isAutoResolved ? 'btn-accent' : 'btn-tinted-success'}`}
        >
          {isAutoResolved ? '⚠ Verify & Save to Knowledge Base' : '✓ Mark as Resolved'}
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {hasRootCause && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
                Review the AI's root cause diagnosis:
              </div>
              <CorrectableField
                label="Summary"
                aiValue={rootCause.summary}
                editing={summaryEditing}
                setEditing={setSummaryEditing}
                text={summaryText}
                setText={setSummaryText}
              />
              <CorrectableField
                label="Details"
                aiValue={rootCause.details}
                editing={detailsEditing}
                setEditing={setDetailsEditing}
                text={detailsText}
                setText={setDetailsText}
              />
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>
              Was the AI's root cause diagnosis correct? *
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              {VERDICT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setVerdict(opt.value)}
                  className={`btn btn-sm ${verdict === opt.value ? 'btn-accent' : 'btn-secondary'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
              What fix was applied? (optional)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Restarted the pod, updated resource limits, fixed env variable..."
              rows={2}
              className="textarea"
              style={{ resize: 'vertical', width: '100%', marginTop: 4 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleResolve}
              disabled={saving || !canConfirm}
              title={!canConfirm ? 'Pick a verdict on the AI diagnosis first' : undefined}
              className={`btn btn-sm ${isAutoResolved ? 'btn-accent' : 'btn-tinted-success'}`}
            >
              {saving ? 'Saving...' : (isAutoResolved ? 'Confirm & Save to Knowledge Base' : 'Confirm Resolved')}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="btn btn-secondary btn-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchInput, setSearchInput] = useState('');   // raw input (debounced)
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [filters, setFilters] = useState({
    status: '',
    severity: '',
    cluster_name: '',
    search: '',
    search_field: 'all',
    page: 1,
    page_size: 20
  });
  const [alertClusters, setAlertClusters] = useState([]);
  const [playbookModal, setPlaybookModal] = useState(null); // { initialForm } or null
  const scrollPosRef = useRef(0);
  const debounceRef = useRef(null);

  // Build pre-filled playbook form from an alert
  const openCreatePlaybook = (alert) => {
    const alertName = alert.alert_name || alert.name || '';
    // Extract a short pattern: remove "above X%" style suffixes, keep key phrase
    const pattern = alertName.replace(/\s+(above|below|over|under)\s+[\d.]+%?/i, '').trim();
    setPlaybookModal({
      initialForm: {
        name:                alertName,
        alert_name_pattern:  pattern,
        server_type:         'k8s',
        severity:            alert.severity || '',
        namespace_pattern:   alert.namespace || '',
        instructions:        '',
        log_paths_raw:       '',
        diagnostic_hints_raw:'',
        is_active:           true,
      }
    });
  };

  useEffect(() => {
    // Clusters from alert data (not registered k8s clusters)
    alertsAPI.getClusters()
      .then(res => setAlertClusters(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    // Save scroll position before re-render
    scrollPosRef.current = window.scrollY;
    try {
      const [alertsResponse, statsResponse] = await Promise.all([
        alertsAPI.list(filters),
        alertsAPI.getStats()
      ]);

      if (alertsResponse.data.alerts) {
        setAlerts(alertsResponse.data.alerts);
        setPagination(alertsResponse.data.pagination);
      } else {
        setAlerts(Array.isArray(alertsResponse.data) ? alertsResponse.data : []);
      }
      setStats(statsResponse.data);

      // Restore scroll position after state update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosRef.current);
      });
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh a single alert in the list (fast — avoids full page reload)
  // Waits 1.5s for ES to index the update before fetching
  const refreshAlert = async (alertId, delayMs = 1500) => {
    await new Promise(r => setTimeout(r, delayMs));
    try {
      const resp = await alertsAPI.get(alertId);
      const updated = resp.data;
      setAlerts(prev => prev.map(a =>
        (a.id || a.alert_id) === (updated.id || updated.alert_id) ? updated : a
      ));
    } catch (e) {
      // fallback to full reload if single-fetch fails
      loadData();
    }
  };

  const handlePageChange = (newPage) => {
    scrollPosRef.current = 0;
    setFilters(f => ({...f, page: newPage}));
  };

  const handlePageSizeChange = (newPageSize) => {
    scrollPosRef.current = 0;
    setFilters(f => ({...f, page: 1, page_size: newPageSize}));
  };

  // Debounced search — triggers server-side ES query after 400ms idle
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters(f => ({...f, search: value, page: 1}));
    }, 400);
  }, []);

  const handleSearchFieldChange = useCallback((field) => {
    // When switching to alert_id search, clear other filters — it's a direct lookup
    if (field === 'alert_id') {
      setFilters(f => ({...f, search_field: field, page: 1, status: '', severity: '', cluster_name: ''}));
    } else {
      setFilters(f => ({...f, search_field: field, page: 1}));
    }
  }, []);

  // No client-side filter needed — server returns pre-filtered results
  const filteredAlerts = alerts;

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'var(--color-danger-500)',
      warning: 'var(--color-warning-500)',
      info: 'var(--color-info-500)'
    };
    return colors[severity] || 'var(--muted)';
  };

  const toggleRow = (alertId) => {
    setExpandedRow(expandedRow === alertId ? null : alertId);
  };

  const formatRelativeTime = (isoString) => {
    if (!isoString) return '-';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)    return 'just now';
    if (mins < 60)   return `${mins}m ago`;
    if (hours < 24)  return `${hours}h ago`;
    if (days < 7)    return `${days}d ago`;
    return new Date(isoString).toLocaleDateString();
  };

  const renderAnnotations = (annotations) => {
    if (!annotations || Object.keys(annotations).length === 0) {
      return <div className="no-annotations">No additional annotations</div>;
    }

    // Separate standard vs extended annotations
    const standardKeys = ['summary', 'description'];
    const extendedAnnotations = Object.entries(annotations)
      .filter(([key]) => !standardKeys.includes(key));

    if (extendedAnnotations.length === 0) {
      return <div className="no-annotations">No additional annotations</div>;
    }

    return (
      <div className="annotations-grid">
        {extendedAnnotations.map(([key, value]) => (
          <div key={key} className="annotation-item">
            <div className="annotation-label">{key.replace(/_/g, ' ').toUpperCase()}</div>
            <div className="annotation-value">{value || '-'}</div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div>Loading alerts...</div>;

  return (
    <div className="alerts-page">
      <h1>Alerts Dashboard</h1>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Alerts</h3>
            <div className="stat-value">{stats.total}</div>
          </div>
          {Object.entries(stats.by_status || {}).map(([status, count]) => (
            <div key={status} className="stat-card">
              <h3>{status}</h3>
              <div className="stat-value">{count}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters + Search */}
      <div className="filters card">
        <div className="search-group">
          <select
            value={filters.search_field}
            onChange={(e) => handleSearchFieldChange(e.target.value)}
            className="filter-select search-field-select"
          >
            <option value="all">All Fields</option>
            <option value="alert_id">Alert ID</option>
            <option value="alert_name">Alert Name</option>
            <option value="pod">Pod</option>
            <option value="namespace">Namespace</option>
            <option value="node">Node</option>
            <option value="cluster">Cluster</option>
            <option value="severity">Severity</option>
            <option value="status">Status</option>
            <option value="summary">Summary</option>
            <option value="description">Description</option>
            <option value="labels">Labels / Instance</option>
          </select>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={
              filters.search_field === 'all' ? 'Search across all 9600 alerts...'
              : filters.search_field === 'alert_id' ? 'e.g. prom-8540410b...'
              : `Search by ${filters.search_field}...`
            }
            className="filter-search"
          />
          {searchInput && (
            <button
              onClick={() => { setSearchInput(''); setFilters(f => ({...f, search: '', page: 1})); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 10px', color: 'var(--muted)', fontSize: 16 }}
              title="Clear search"
            >×</button>
          )}
        </div>

        <select
          value={filters.severity}
          onChange={(e) => setFilters({...filters, severity: e.target.value, page: 1})}
          className="filter-select"
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>

        <select
          value={filters.status}
          onChange={(e) => setFilters({...filters, status: e.target.value, page: 1})}
          className="filter-select"
        >
          <option value="">All Statuses</option>
          <option value="firing">Firing</option>
          <option value="resolved">Resolved</option>
        </select>

        <select
          value={filters.cluster_name}
          onChange={(e) => setFilters({...filters, cluster_name: e.target.value, page: 1})}
          className="filter-select"
        >
          <option value="">All Clusters</option>
          {alertClusters.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      {/* Alerts Table */}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Severity</th>
              <th>Alert Name</th>
              <th>Cluster</th>
              <th>Pod/Node</th>
              <th>Status</th>
              <th>Since</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {filteredAlerts.length === 0 ? (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', color: 'var(--muted)', padding: '24px 0'}}>
                  {filters.search ? `No alerts matching "${filters.search}"` : 'No alerts found'}
                </td>
              </tr>
            ) : (
              filteredAlerts.map((alert) => (
                <React.Fragment key={alert.alert_id}>
                  <tr
                    onClick={() => toggleRow(alert.alert_id)}
                    className={`alert-row ${expandedRow === alert.alert_id ? 'expanded' : ''}`}
                    style={{cursor: 'pointer'}}
                  >
                    <td>
                      <span
                        className="severity-badge"
                        style={{backgroundColor: getSeverityColor(alert.severity)}}
                      >
                        {alert.severity}
                      </span>
                    </td>
                    <td>
                      <span className="expand-icon">
                        {expandedRow === alert.alert_id ? '▼' : '▶'}
                      </span>
                      {alert.alert_name}
                    </td>
                    <td>{alert.cluster_name || alert.labels?.job || '-'}</td>
                    <td>{alert.pod_name || alert.node_name || '-'}</td>
                    <td>
                      <span className={`status-badge status-${alert.status}`}>
                        {alert.status}
                      </span>
                    </td>
                    <td title={new Date(alert.starts_at).toLocaleString()}>{formatRelativeTime(alert.starts_at)}</td>
                    <td>{alert.summary}</td>
                  </tr>
                  {expandedRow === alert.alert_id && (
                    <tr className="expanded-content">
                      <td colSpan="7">
                        <div className="alert-details">
                          <div className="details-section">
                            <h4>Alert Details</h4>
                            <div className="detail-row">
                              <span className="detail-label">Alert ID:</span>
                              <span className="detail-value">{alert.alert_id}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Started At:</span>
                              <span className="detail-value">{new Date(alert.starts_at).toLocaleString()}</span>
                            </div>
                            {alert.ends_at && (
                              <div className="detail-row">
                                <span className="detail-label">Ended At:</span>
                                <span className="detail-value">{new Date(alert.ends_at).toLocaleString()}</span>
                              </div>
                            )}
                            <div className="detail-row">
                              <span className="detail-label">Description:</span>
                              <span className="detail-value">{alert.annotations?.description || alert.description || '-'}</span>
                            </div>
                          </div>

                          {alert.lifecycle_events?.length > 0 && (
                            <div className="details-section">
                              <h4>Lifecycle</h4>
                              {/* Fired-at and resolved-at as distinct timestamped events under
                                  the SAME alert_id — this is one document, overwritten in place
                                  as status changes, so the firing-moment data would otherwise be
                                  gone the instant it resolved. lifecycle_events preserves both. */}
                              <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                                {alert.lifecycle_events.map((ev, i) => (
                                  <div key={i} style={{ fontSize: 13 }}>
                                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{ev.status}</span>
                                    {' — '}
                                    {new Date(ev.timestamp).toLocaleString()}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="details-section">
                            <h4>Additional Annotations</h4>
                            {renderAnnotations(alert.annotations)}
                          </div>

                          {alert.labels && Object.keys(alert.labels).length > 0 && (
                            <div className="details-section">
                              <h4>Labels</h4>
                              <div className="annotations-grid">
                                {Object.entries(alert.labels).map(([key, value]) => (
                                  <div key={key} className="annotation-item">
                                    <div className="annotation-label">{key.toUpperCase()}</div>
                                    <div className="annotation-value">{value}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Step 1: Diagnostics + Analyze with AI (pod/node alerts) */}
                          {(alert.pod_name || alert.node_name) && (
                            <div className="details-section">
                              <Troubleshoot
                                alertId={alert.id || alert.alert_id}
                                podName={alert.pod_name}
                                nodeName={alert.node_name}
                                alert={alert}
                                onAnalysisDone={() => {
                                  setTimeout(loadData, 1000);
                                }}
                              />
                            </div>
                          )}

                          {/* For non-pod alerts: standalone Analyze with AI */}
                          {!alert.pod_name && !alert.node_name && (
                            <div className="details-section ai-section">
                              <AIAnalysis
                                alertId={alert.id || alert.alert_id}
                                initialAnalysis={alert.ai_analysis}
                              />
                            </div>
                          )}

                          {/* AI Analysis result (pod/node alerts — shown after Troubleshoot) */}
                          {(alert.pod_name || alert.node_name) && alert.ai_analysis && (
                            <div className="details-section ai-section">
                              <AIAnalysis
                                alertId={alert.id || alert.alert_id}
                                initialAnalysis={alert.ai_analysis}
                              />
                            </div>
                          )}

                          {/* Create Playbook from this alert */}
                          <div className="details-section" style={{ paddingBottom: 0 }}>
                            <button
                              className="create-playbook-btn"
                              onClick={() => openCreatePlaybook(alert)}
                              title="Pre-fill a new playbook with this alert's name, severity and namespace — add instructions and diagnostic commands, then save"
                            >
                              + Create Playbook for this Alert Type
                            </button>
                          </div>

                          {/* Resolution Plan — always shown so user can generate fix plan */}
                          <div className="details-section">
                            <ResolutionPlan
                              alert={alert}
                              onPlanUpdated={() => refreshAlert(alert.id || alert.alert_id)}
                            />
                          </div>

                          {/* Mark Resolved */}
                          <MarkResolved
                            alert={alert}
                            onResolved={loadData}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {alerts.length > 0 ? ((pagination.page - 1) * pagination.page_size + 1) : 0} to {Math.min(pagination.page * pagination.page_size, pagination.total)} of {pagination.total} alerts
          </div>

          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={!pagination.has_prev}
              className="pagination-btn"
            >
              &laquo; First
            </button>

            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={!pagination.has_prev}
              className="pagination-btn"
            >
              &lsaquo; Previous
            </button>

            <span className="pagination-pages">
              Page {pagination.page} of {pagination.total_pages}
            </span>

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.has_next}
              className="pagination-btn"
            >
              Next &rsaquo;
            </button>

            <button
              onClick={() => handlePageChange(pagination.total_pages)}
              disabled={!pagination.has_next}
              className="pagination-btn"
            >
              Last &raquo;
            </button>
          </div>

          <div className="page-size-selector">
            <label>Per page:</label>
            <select
              value={pagination.page_size}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="page-size-select"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Create Playbook modal — opened from any alert's detail row */}
      {playbookModal && (
        <PlaybookFormModal
          initialForm={playbookModal.initialForm}
          onSaved={() => setPlaybookModal(null)}
          onClose={() => setPlaybookModal(null)}
        />
      )}
    </div>
  );
};

export default AlertsPage;
